import { useEffect, useRef, useState } from 'react';
import {
  activeGameOptions,
  BiddingGameState,
  GAME_OPTION_COPY,
  GameStateStatus,
  PlayerTableViewModelV2,
  Suite,
  Team,
  TrickCard,
} from 'shared';
import { Panel } from '../../ui/Panel';
import { Text } from '../../ui/Text';
import { Card } from '../../ui/Card';
import { teamColor, token, zVar } from '../../ui/theme';
import { CodeChip } from './game-code';
import { CopyInviteButton } from './invite-button';
import { LastTrickPeek, LastTrickData } from './last-trick-peek';
import { SetHistoryPeek } from './set-history-peek';

/*
 * GameInfoPanel — the single HUD window (bottom-left) describing the live game.
 * Combines the old CurrentBid, TrumpBadge, Scoreboard and TrickWinnings widgets:
 *
 *   Game   — score to win, trump suit, current bid (+ who owns it), plus any
 *            non-default rules the host turned on
 *   Teams  — members, score, and this-hand winnings per team
 *
 * The winnings breakdown (captured cards, grouped into tricks) is collapsed by
 * default and expands inline per team (accordion) so the panel stays digestible.
 */

interface Props {
  table: PlayerTableViewModelV2;
  /** The previous completed trick, surfaced via the panel's "Last trick" peek. */
  lastTrick?: LastTrickData;
}

const SUIT_GLYPH: Record<Suite, string> = {
  [Suite.Hearts]: '♥',
  [Suite.Diamonds]: '♦',
  [Suite.Spades]: '♠',
  [Suite.Clubs]: '♣',
};
const isRedSuit = (s: Suite) => s === Suite.Hearts || s === Suite.Diamonds;

// Card display size inside the expanded winnings breakdown.
const CARD_W = 40;
const CARD_H = 56;

/** Matches the --dur-flash token driving the .ui-flash animation. */
const FLASH_MS = 900;

export const GameInfoPanel: React.FC<Props> = ({ table, lastTrick }) => {
  const [openTeamIndex, setOpenTeamIndex] = useState<number>();

  const gs = table.gameState;
  if (!gs) return null;

  const { teams, scoreToWin } = gs;
  const seatCount = table.tablePlayers.filter(Boolean).length || 1;

  // Trump only exists once a trick is underway.
  const trumpSuite = 'trumpSuite' in gs ? (gs.trumpSuite as Suite) : undefined;

  // Bid + owner: live count during bidding, the won contract afterwards.
  let bid = 0;
  let bidderId: string | undefined;
  if (gs.status === GameStateStatus.Bidding) {
    bid = (gs as BiddingGameState).currentBid;
    bidderId = (gs as BiddingGameState).currentBiddingPlayer;
  } else if ('bidForSet' in gs) {
    bid = gs.bidForSet;
    bidderId = gs.biddingPlayer;
  }

  const nameOf = (id?: string) => table.tablePlayers.find((p) => p?.id === id)?.name;
  const namesOf = (team: Team) =>
    team.players
      .map((id) => nameOf(id))
      .filter(Boolean)
      .join(' & ') || '—';

  const bidderName = nameOf(bidderId);
  const biddingTeamId = teams.find((t) => t.players.includes(bidderId ?? ''))?.id;

  // Split a team's flat collection into per-trick chunks (one isWinner each).
  const tricksOf = (collection: TrickCard[]): TrickCard[][] => {
    const tricks: TrickCard[][] = [];
    for (let i = 0; i < collection.length; i += seatCount) {
      tricks.push(collection.slice(i, i + seatCount));
    }
    return tricks;
  };

  return (
    <Panel
      variant="hud"
      style={{
        position: 'absolute',
        left: 24,
        top: 8,
        zIndex: zVar('z-hud'),
        width: 280,
        maxHeight: 720,
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
        overflow: 'hidden',
      }}
    >
      {/* Name + code + invite, kept visible during play so it can be shared. */}
      <div style={{ display: 'grid', gap: 6, marginBottom: -4 }}>
        <Text
          variant="value"
          as="div"
          style={{
            fontSize: 'var(--font-size-md)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={table.tableTitle}
        >
          {table.tableTitle}
        </Text>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-2)',
          }}
        >
          <CodeChip code={table.tableCode} />
          <CopyInviteButton tableCode={table.tableCode} label="Invite" />
        </div>
      </div>

      {/* ── Game ─────────────────────────────────────────── */}
      <div>
        <Text variant="label">Game</Text>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-2)',
          }}
        >
          <Stat label="Win at" value={String(scoreToWin)} />
          <Stat
            label="Bid"
            value={<span style={{ color: token('color-gold') }}>{bid > 0 ? bid : '—'}</span>}
            sub={bidderName}
          />
          <Stat
            label="Trump"
            value={
              trumpSuite ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)',
                    color: isRedSuit(trumpSuite) ? token('color-lose') : token('color-text'),
                  }}
                >
                  <span style={{ fontSize: 'var(--font-size-lg)', lineHeight: 1 }}>
                    {SUIT_GLYPH[trumpSuite]}
                  </span>
                  <span style={{ textTransform: 'capitalize' }}>{trumpSuite}</span>
                </span>
              ) : (
                '—'
              )
            }
          />
        </div>

        <ActiveRules table={table} />
      </div>

      <Divider />

      {/* ── Teams ────────────────────────────────────────── */}
      <div style={{ minHeight: 0, overflowY: 'auto' }}>
        <Text variant="label">Teams</Text>
        <div
          style={{
            display: 'grid',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-2)',
          }}
        >
          {teams.map((team, i) => {
            const isOpen = openTeamIndex === i;
            const cards = team.collection.length;
            const points = team.collection.reduce((s, c) => s + c.pointValue, 0);
            const tricks = Math.floor(cards / seatCount);
            const isBidder = team.id === biddingTeamId;

            return (
              <div key={team.id}>
                <button
                  type="button"
                  className="ui-bare-btn"
                  onClick={() => setOpenTeamIndex(isOpen ? undefined : i)}
                  disabled={cards === 0}
                  aria-expanded={isOpen}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    width: '100%',
                    padding: 'var(--space-2)',
                    borderRadius: 'var(--radius-md)',
                    background: token('color-surface-2'),
                    cursor: cards === 0 ? 'default' : 'pointer',
                  }}
                >
                  <span
                    style={{
                      width: 12,
                      transition: 'transform 120ms',
                      transform: isOpen ? 'rotate(90deg)' : 'none',
                      color: token('color-muted'),
                      opacity: cards === 0 ? 0.3 : 1,
                    }}
                  >
                    ▶
                  </span>
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 'var(--radius-pill)',
                      background: teamColor(i),
                      flex: 'none',
                    }}
                  />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        color: token('color-text'),
                        fontSize: 'var(--font-size-sm)',
                      }}
                    >
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {namesOf(team)}
                      </span>
                      {isBidder && <BidChip />}
                    </span>
                    <span
                      style={{
                        display: 'block',
                        color: token('color-muted'),
                        fontSize: 'var(--font-size-xs)',
                        marginTop: 2,
                      }}
                    >
                      {cards === 0
                        ? 'No winnings yet'
                        : `${tricks} ${tricks === 1 ? 'trick' : 'tricks'} · ${points} ${
                            points === 1 ? 'pt' : 'pts'
                          }`}
                    </span>
                  </span>
                  <TeamScore score={team.score} color={teamColor(i)} />
                </button>

                {isOpen && cards > 0 && (
                  <div
                    style={{
                      display: 'grid',
                      gap: 'var(--space-3)',
                      padding: 'var(--space-3) var(--space-2) var(--space-1)',
                    }}
                  >
                    {tricksOf(team.collection).map((trick, ti) => (
                      <div key={ti} style={{ display: 'grid', gap: 'var(--space-1)' }}>
                        <Text variant="label">Trick {ti + 1}</Text>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
                          {trick.map((card, ci) => (
                            <Card
                              key={`${card.imageName}-${ci}`}
                              code={card.imageName}
                              width={CARD_W}
                              height={CARD_H}
                              style={
                                card.isWinner
                                  ? {
                                      outline: `2px solid ${token('color-gold')}`,
                                      outlineOffset: -1,
                                    }
                                  : undefined
                              }
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Peek buttons: previous completed trick + running set history. */}
      {(!!lastTrick?.cards.length || gs.setHistory.length > 0) && (
        <>
          <Divider />
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <LastTrickPeek lastTrick={lastTrick} />
            <SetHistoryPeek table={table} />
          </div>
        </>
      )}
    </Panel>
  );
};

/*
 * The rule variants this table is playing by. Nothing renders on a standard
 * game — the point is to make a non-standard one impossible to miss, since the
 * rules change how the score moves.
 */
const ActiveRules: React.FC<{ table: PlayerTableViewModelV2 }> = ({ table }) => {
  const rules = activeGameOptions(table.options);
  if (rules.length === 0) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--space-1)',
        marginTop: 'var(--space-2)',
      }}
    >
      {rules.map((rule) => (
        <span key={rule} style={ruleChip} title={GAME_OPTION_COPY[rule].detail}>
          {GAME_OPTION_COPY[rule].label}
        </span>
      ))}
    </div>
  );
};

const ruleChip: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  color: token('color-accent'),
  border: `1px solid ${token('color-accent')}`,
  borderRadius: 'var(--radius-pill)',
  padding: '0 6px',
  lineHeight: 1.6,
};

// A team's running score, popped briefly whenever it changes (end of a set).
const TeamScore: React.FC<{ score: number; color: string }> = ({ score, color }) => {
  const [flashing, setFlashing] = useState(false);
  const previous = useRef(score);

  useEffect(() => {
    if (previous.current === score) return;
    previous.current = score;
    setFlashing(true);
    const t = window.setTimeout(() => setFlashing(false), FLASH_MS);
    return () => window.clearTimeout(t);
  }, [score]);

  return (
    <Text variant="value" color={color} className={flashing ? 'ui-flash' : undefined}>
      {score}
    </Text>
  );
};

// A stacked label/value cell used in the Game row.
const Stat: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: string;
}> = ({ label, value, sub }) => (
  <div style={{ display: 'grid', gap: 2, alignContent: 'start' }}>
    <Text variant="label">{label}</Text>
    <div
      style={{
        fontSize: 'var(--font-size-lg)',
        fontWeight: 700,
        lineHeight: 1.1,
      }}
    >
      {value}
    </div>
    {sub && (
      <Text as="div" variant="muted" style={{ fontSize: 'var(--font-size-xs)' }}>
        {sub}
      </Text>
    )}
  </div>
);

const BidChip: React.FC = () => (
  <span
    style={{
      flex: 'none',
      fontSize: 'var(--font-size-xs)',
      fontWeight: 700,
      letterSpacing: 'var(--letter-spacing-wide)',
      color: token('color-gold'),
      border: `1px solid ${token('color-gold')}`,
      borderRadius: 'var(--radius-pill)',
      padding: '0 6px',
      lineHeight: 1.6,
    }}
  >
    BID
  </span>
);

const Divider: React.FC = () => (
  <div style={{ height: 1, background: token('color-border'), opacity: 0.6 }} />
);

export default GameInfoPanel;
